import Deferred from '@/core/async/Deferred';
import debounce from 'lodash-es/debounce';

export abstract class AbstractBulkFetch<T> {
	protected activeListenersById: Map<any, Deferred<T>[]> = new Map<string, Deferred<T>[]>();
	protected debouncedFetch = debounce(this.fetch, 100);

	constructor(protected controller: string) {}

	public async requestData(id: string | number): Promise<T> {
	    const deferred = new Deferred<T>();

	    const listeners = this.activeListenersById.get(id);
	    if (listeners) {
	        listeners.push(deferred);
	    } else {
	        this.activeListenersById.set(id, [deferred]);
	    }

	    this.debouncedFetch();
	    return deferred.promise;
	}

	async fetch() {
	    const listeners = this.activeListenersById;
	    this.activeListenersById = new Map<any, Deferred<T>[]>();

	    try {
	        const ids = Array.from(listeners.keys());
	        const result = await this.apiCall(ids);
	        this.handleResultFromServer(listeners, result);
	    } catch (e) {
	        this.handleResultFromServer(listeners, null);
	        throw new Error(e);
	    }
	}

	handleResultFromServer(listeners: Map<any, Deferred<T>[]>, result: { [key: string]: T } | null) {
	    listeners.forEach((listener: Deferred<T>[], id: string | number) => {
	        const data = result && result[id];
	        if (data) {
	            listener.forEach(l => l.resolve(data));
	        } else {
	            listener.forEach(l => l.reject(`[${this.controller}]: Unable to find a result for id: ${id}`));
	        }
	    });
	}

	protected getUrl(action: string): string {
	    return `${this.controller}/${action}`;
	}

	protected abstract async apiCall(ids: string[] | number[]): Promise<{ [key: string]: T }>;
}

export abstract class CachedBulkFetchService<T> extends AbstractBulkFetch<T> {
	cache = new Map<any, T>();

	protected async getCachedItem(id: string | number): Promise<T> {
	    const item = this.cache.get(id);
	    if (item) return Promise.resolve(item);

	    const result = await this.requestData(id);
	    this.setCachedItem(id, result);
	    return result;
	}

	protected setCachedItem(id: string | number, value: T) {
	    this.cache.set(id, value);
	}
}
